import React from 'react';
import { ArrowLeft, ShieldCheck } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import BrandLogo from '../ui/BrandLogo';
import { useSeoHead } from '../public/SeoHead';

const PrivacyPolicy: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useSeoHead({
    title: 'Privacy Policy | mapbl',
    description: 'How mapbl handles public profiles, map locations, statuses, reactions, and account information.',
    canonicalPath: '/privacy',
  });

  return (
    <main className="relative min-h-screen-safe overflow-hidden bg-slate-50 px-4 py-10 text-slate-900 dark:bg-slate-950 dark:text-white sm:px-6">
      <div aria-hidden="true" className="absolute -left-28 -top-28 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl dark:bg-cyan-400/15" />
      <div aria-hidden="true" className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-violet-500/20 blur-3xl dark:bg-violet-500/15" />

      <div className="relative mx-auto max-w-3xl">
        <div className="mb-8 flex items-center justify-between gap-4">
          <BrandLogo className="h-auto w-36" />
          <Link
            to={isAuthenticated ? '/app' : '/login'}
            className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-200 transition hover:text-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 dark:bg-white/[0.06] dark:text-slate-300 dark:ring-white/10 dark:hover:text-cyan-200"
          >
            <ArrowLeft size={17} />
            {isAuthenticated ? 'Back to map' : 'Back to sign in'}
          </Link>
        </div>

        <article className="rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_24px_80px_rgba(2,6,23,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-10">
          <div className="mb-8">
            <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-300">
              <ShieldCheck size={18} weight="duotone" /> Privacy &amp; play
            </div>
            <h1 className="text-4xl font-semibold tracking-[-0.04em]">Privacy Policy</h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Effective September 7, 2026</p>
          </div>

          <div className="space-y-7 text-[15px] leading-7 text-slate-600 dark:text-slate-300">
            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">A fantasy map made for fun</h2>
              <p>
                mapbl is a game and creative social experience. Names, profiles, locations, statuses, events, announcements, and other content shown on the map are user-submitted, are not independently verified, and must not be treated as statements of fact. Everything you see should be understood as fictional, role-play, or fantasy content created for entertainment only.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">Information used by the game</h2>
              <p>
                We process only the information needed to provide the experience: account details you submit, such as your email, display name, bio, avatar, and password in protected hashed form; statuses and reactions you create; a browser-selected or manually selected map location; and sign-in tokens stored on your device. This information is saved only so the game can operate and remember your account and game activity.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">What other players can see</h2>
              <p>
                The experience is built around a shared map. Your chosen game identity, avatar, bio, approximate or selected location, statuses, and reactions may be visible to other players. Your password and authentication tokens are not displayed to other players. Do not use real-world secrets, sensitive personal details, or a precise real-world location in your public game content.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">How information is used</h2>
              <p>
                Submitted information is used only to authenticate you, display and personalize the map, show game content, support nearby discovery, and maintain the service. mapbl does not sell your personal information or use your game activity to make real-world claims about you.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">Maps and service providers</h2>
              <p>
                The site may rely on hosting, map, and geocoding providers to deliver the service. Those providers may receive ordinary technical requests, such as an IP address and map coordinates, under their own privacy terms. We limit access to stored information to what is necessary to operate and protect the game.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">Your choices</h2>
              <p>
                You choose what identity, status, and location to submit. You can update your profile and game content through the available controls, stop sharing a browser location by changing your browser permissions, and sign out to remove sign-in tokens from the device. Please contact the site operator if you need help with your account or stored information.
              </p>
            </section>

            <section>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">Changes to this policy</h2>
              <p>
                We may update this policy as the game changes. The effective date at the top will be revised when that happens. Continued use of mapbl after an update means you accept the revised policy.
              </p>
            </section>
          </div>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
